import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { isEmail } from "validator";

import authService from "../../../services/auth.service";
import UserDataService from "../../../pages/services/UserService";

import {
  Container,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { LoadingButton } from '@mui/lab';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AutoAwesome from '@mui/icons-material/AutoAwesome';

import isValid from 'date-fns/isValid';

// Clean string by removing titles, punctuation, and extra whitespace for fuzzy match
const cleanNameTokens = (str) => {
  if (!str || typeof str !== 'string') return [];
  const clean = str
    .toLowerCase()
    .replace(/\b(dr|doctor|mr|mrs|miss|ms|alhaji|alhaja|engr|engineer|chief|imam|pastor|barrister|barr)\b\.?/gi, ' ')
    .replace(/[^a-z0-9\s]/gi, ' ')
    .trim();
  return clean.split(/\s+/).filter((w) => w.length > 2);
};

// Find closest matching member for a typed referee string
const findSuggestedRefereeMatch = (typedName, options) => {
  if (!typedName || typeof typedName !== 'string' || typedName.trim().length < 3) return null;
  const typedTokens = cleanNameTokens(typedName);
  if (typedTokens.length === 0) return null;

  let bestMatch = null;
  let highestScore = 0;

  for (const opt of options) {
    const memberTokens = cleanNameTokens(opt.fullName || opt.label);
    if (memberTokens.length === 0) continue;

    let matchedTokens = 0;
    for (const t of typedTokens) {
      if (memberTokens.some((m) => m.includes(t) || t.includes(m))) {
        matchedTokens++;
      }
    }

    const score = matchedTokens / Math.max(typedTokens.length, memberTokens.length);
    if (score >= 0.5 && score > highestScore) {
      highestScore = score;
      bestMatch = opt;
    }
  }

  return highestScore >= 0.5 ? bestMatch : null;
};

const NIGERIAN_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","Gombe","Imo","Jigawa",
  "Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa","Niger",
  "Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba","Yobe","Zamfara","FCT"
];

const SignupForm = () => {
  const formWrapperStyle = { maxWidth: '100%', margin: 0, px: 0, width: '100%', boxSizing: 'border-box' };
  const headerBarStyle = {
    padding: { xs: '8px 12px', sm: '12px 18px' },
    display: 'flex',
    justifyContent: { xs: 'flex-start', sm: 'space-between' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: 1, sm: 12 },
    flexWrap: 'wrap',
    flexDirection: { xs: 'column', sm: 'row' }
  };

  const maxDobDate = new Date();
  maxDobDate.setFullYear(maxDobDate.getFullYear() - 18);
  const formatDate = (d) => d.toISOString().split('T')[0];
  const maxDob = formatDate(maxDobDate);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sex, setSex] = useState("");
  const [sexError, setSexError] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [state, setState] = useState("");
  const [homeTown, setHomeTown] = useState("");
  const [applicantsPlaceOfWork, setApplicantsPlaceOfWork] = useState("");
  const [applicantDate, setApplicantDate] = useState("");
  const [nameOfReferee, setNameOfReferee] = useState("");
  const [refereeAddress, setRefereeAddress] = useState("");
  const [refereePhoneNumber, setRefereePhoneNumber] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [refereeDate, setRefereeDate] = useState("");
  const [residenceAddress, setResidenceAddress] = useState("");
  const [postalAddress, setPostalAddress] = useState("");
  const [approvedOrNotApproved, setApprovedOrNotApproved] = useState("Active");
  const [statusDate, setStatusDate] = useState("");
  const [registrationNo, setRegistrationNo] = useState("");
  const [registrationError, setRegistrationError] = useState("");
  const [localMessage, setLocalMessage] = useState("");
  const [successful, setSuccessful] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordManuallyEdited, setPasswordManuallyEdited] = useState(false);

  // Referee members list for autocomplete lookup
  const [refereeMembers, setRefereeMembers] = useState([]);
  const [loadingReferees, setLoadingReferees] = useState(false);

  useEffect(() => {
    setLoadingReferees(true);
    UserDataService.getRefereeMembers()
      .then((res) => {
        const list = res?.data || res || [];
        setRefereeMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setRefereeMembers([]);
      })
      .finally(() => setLoadingReferees(false));
  }, []);

  const refereeOptions = useMemo(() => {
    return refereeMembers
      .map((m) => {
        const mid = String(m._id || m.id);
        const fname = (m.firstName || m.firstname || m.first_name || "").trim();
        const lname = (m.lastName || m.lastname || m.last_name || "").trim();
        const fullName = (lname ? `${lname} ${fname}`.trim() : fname) || (m.username || m.name || "").trim() || "Member";
        const regNo = (m.registrationNo || m.membershipId || "").trim();
        const label = regNo ? `${fullName} (${regNo})` : fullName;
        return {
          id: mid,
          label,
          fullName,
          regNo,
          raw: m,
        };
      })
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [refereeMembers]);

  // Smart Form Assistant: Find match if referee name is typed but phone or email is missing
  const suggestedRefereeMatch = useMemo(() => {
    if (!nameOfReferee || nameOfReferee.trim().length < 3) return null;
    return findSuggestedRefereeMatch(nameOfReferee, refereeOptions);
  }, [nameOfReferee, refereeOptions]);

  const handleApplySuggestedReferee = (match) => {
    if (!match) return;
    setNameOfReferee(match.fullName || match.label || "");
    if (match.raw?.phoneNumber) setRefereePhoneNumber(match.raw.phoneNumber);
    if (match.raw?.email) setRefereeEmail(match.raw.email);
    if (match.raw?.residenceAddress || match.raw?.postalAddress) {
      setRefereeAddress(match.raw.residenceAddress || match.raw.postalAddress || "");
    }
  };

  const [dateErrors, setDateErrors] = useState({
    date: "",
    applicantDate: "",
    refereeDate: "",
    statusDate: ""
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const { message } = useSelector(state => state.message);

  const onChangeUsername = (e) => setUsername(e.target.value);
  const onChangeEmail = (e) => setEmail(e.target.value);
  const onChangeDate = (e) => setDate(e.target.value);
  const onChangeFirstName = (e) => setFirstName(e.target.value);
  const onChangeMiddleName = (e) => setMiddleName(e.target.value);
  const onChangeLastName = (e) => setLastName(e.target.value);
  const onChangePhoneNumber = (e) => setPhoneNumber(e.target.value);
  const onChangeSex = (e) => setSex(e.target.value);
  const onChangeMaritalStatus = (e) => setMaritalStatus(e.target.value);
  const onChangeState = (e) => setState(e.target.value);
  const onChangeHomeTown = (e) => setHomeTown(e.target.value);
  const onChangeApplicantsPlaceOfWork = (e) => setApplicantsPlaceOfWork(e.target.value);
  const onChangeApplicantDate = (e) => setApplicantDate(e.target.value);
  const onChangeRefereeAddress = (e) => setRefereeAddress(e.target.value);
  const onChangeRefereePhoneNumber = (e) => setRefereePhoneNumber(e.target.value);
  const onChangeRefereeEmail = (e) => setRefereeEmail(e.target.value);
  const onChangeRefereeDate = (e) => setRefereeDate(e.target.value);
  const onChangeResidenceAddress = (e) => setResidenceAddress(e.target.value);
  const onChangePostalAddress = (e) => setPostalAddress(e.target.value);
  const onChangeApprovedOrNotApproved = (e) => setApprovedOrNotApproved(e.target.value);
  const onChangeStatusDate = (e) => setStatusDate(e.target.value);
  const onChangeRegistrationNo = (e) => setRegistrationNo(e.target.value.replace(/\D/g, '').slice(0, 6));

  const validatePhone = (phone) => {
    if (!phone) return false;
    const cleaned = phone.toString().trim().replace(/[\s-.()]/g, '');
    if (/^(?:\+234)\d{10}$/.test(cleaned) || /^0\d{10}$/.test(cleaned)) return true;
    if (/^(?:\+44)\d{10}$/.test(cleaned) || /^0\d{10}$/.test(cleaned)) return true;
    if (/^(?:\+1)\d{10}$/.test(cleaned) || /^\d{10}$/.test(cleaned)) return true;
    return false;
  };

  const generatedPassword = useMemo(() => {
    if (!registrationNo) return "";
    let g = (username || "") + registrationNo.slice(-3);
    if (g.length < 6) g = g.padEnd(6, "0");
    return g;
  }, [username, registrationNo]);

  // Sync the password field with the auto-generated value unless admin manually changed it
  useEffect(() => {
    if (!passwordManuallyEdited) {
      setPassword(generatedPassword);
    }
  }, [generatedPassword, passwordManuallyEdited]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.debug("copy failed", e);
    }
  };

  const resetForm = () => {
    setUsername("");
    setEmail("");
    setDate("");
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setPhoneNumber("");
    setSex("");
    setSexError("");
    setMaritalStatus("");
    setState("");
    setHomeTown("");
    setApplicantsPlaceOfWork("");
    setApplicantDate("");
    setNameOfReferee("");
    setRefereeAddress("");
    setRefereePhoneNumber("");
    setRefereeEmail("");
    setRefereeDate("");
    setResidenceAddress("");
    setPostalAddress("");
    setApprovedOrNotApproved("Active");
    setStatusDate("");
    setRegistrationNo("");
    setRegistrationError("");
    setLocalMessage("");
    setFieldErrors({});
    setDateErrors({ date: "", applicantDate: "", refereeDate: "", statusDate: "" });
    setSuccessful(false);
    setPassword("");
    setPasswordManuallyEdited(false);
    setShowPassword(false);
  };

  const renderError = (field) => {
    if (fieldErrors[field]) return <Box sx={{ mt: 1 }}><Alert severity="error">{fieldErrors[field]}</Alert></Box>;
    if (dateErrors[field]) return <Box sx={{ mt: 1 }}><Alert severity="error">{dateErrors[field]}</Alert></Box>;
    if (field === 'sex' && sexError) return <Box sx={{ mt: 1 }}><Alert severity="error">{sexError}</Alert></Box>;
    if (field === 'registrationNo' && registrationError) return <Box sx={{ mt: 1 }}><Alert severity="error">{registrationError}</Alert></Box>;
    return null;
  }

  const handleRegister = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSuccessful(false);
    setLoading(true);

    setSexError("");
    setRegistrationError("");
    setDateErrors({ date: "", applicantDate: "", refereeDate: "", statusDate: "" });
    setFieldErrors({});

    const fErr = {};
    if (!username) fErr.username = "Username is required.";
    if (!email) fErr.email = "Email is required.";
    else if (!isEmail(email)) fErr.email = "Invalid email.";
    if (!date) fErr.date = "Date of birth is required.";
    if (!firstName) fErr.firstName = "First name is required.";
    if (!lastName) fErr.lastName = "Last name is required.";
    if (!phoneNumber) fErr.phoneNumber = "Phone number is required.";
    else if (!validatePhone(phoneNumber)) fErr.phoneNumber = "Invalid phone number.";
    if (!maritalStatus) fErr.maritalStatus = "Marital status is required.";
    if (!sex) fErr.sex = "Please select gender.";
    if (!residenceAddress) fErr.residenceAddress = "Home address is required.";
    if (!applicantsPlaceOfWork) fErr.applicantsPlaceOfWork = "Occupation is required.";
    if (!nameOfReferee) fErr.nameOfReferee = "Referee name is required.";
    if (!registrationNo) fErr.registrationNo = "Registration number is required.";
    else if (!/^\d{6}$/.test(registrationNo.trim())) fErr.registrationNo = "Registration number must be exactly 6 digits.";
    if (!password || password.length < 6) fErr.password = "Password must be at least 6 characters.";

    if (refereePhoneNumber && !validatePhone(refereePhoneNumber)) fErr.refereePhoneNumber = "Invalid referee phone number.";
    if (refereeEmail && !isEmail(refereeEmail)) fErr.refereeEmail = "Invalid referee email.";

    const newDateErrors = {};
    if (!date || !isValid(new Date(date))) {
      newDateErrors.date = "Please select a valid date.";
    } else if (new Date(date) > maxDobDate) {
      newDateErrors.date = "Member must be at least 18 years old.";
    }
    if (applicantDate && !isValid(new Date(applicantDate))) newDateErrors.applicantDate = "Please select a valid applicant date.";
    if (refereeDate && !isValid(new Date(refereeDate))) newDateErrors.refereeDate = "Please select a valid referee date.";
    if (statusDate && !isValid(new Date(statusDate))) newDateErrors.statusDate = "Please select a valid status date.";

    const hasFieldErrors = Object.keys(fErr).length > 0;
    const hasDateErrs = Object.keys(newDateErrors).length > 0;
    if (hasFieldErrors || hasDateErrs) {
      setFieldErrors(fErr);
      setDateErrors(prev => ({ ...prev, ...newDateErrors }));
      setLoading(false);
      return;
    }

    try {
      const resp = await authService.register(
        username,
        email,
        password,
        date ? new Date(date).toISOString() : null,
        firstName,
        middleName,
        lastName,
        phoneNumber,
        sex,
        maritalStatus,
        state,
        homeTown,
        applicantsPlaceOfWork,
        null, // applicantSignature
        applicantDate ? new Date(applicantDate).toISOString() : null,
        nameOfReferee,
        refereeAddress,
        refereePhoneNumber,
        refereeEmail,
        null, // refereeSignature
        refereeDate ? new Date(refereeDate).toISOString() : null,
        residenceAddress,
        postalAddress,
        approvedOrNotApproved,
        statusDate ? new Date(statusDate).toISOString() : null,
        registrationNo
      );
      if (resp && (resp.status === 201 || resp.status === 200)) {
        setSuccessful(true);
        setLocalMessage(resp?.data?.message || "Registration successful.");
      } else {
        const msg = resp?.data?.message || `Registration failed (${resp?.status})`;
        setRegistrationError(msg);
        setLocalMessage(msg);
        setSuccessful(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Registration failed";
      setRegistrationError(msg);
      setLocalMessage(msg);
      setSuccessful(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container sx={formWrapperStyle} maxWidth={false} disableGutters>
      <Paper elevation={6} sx={{ borderRadius: { xs: 0, sm: 2 }, overflow: 'hidden', width: '100%', mx: 0 }}>
        <Box sx={headerBarStyle}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PersonAddIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Register Member</Typography>
              <Typography variant="body2" color="text.secondary">Create a new member account quickly and securely</Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: { xs: 0.5, sm: 0 }, alignSelf: { xs: 'flex-end', sm: 'auto' } }}
          >
            <Button component={Link} to="/dashboard" variant="outlined">Back to Dashboard</Button>
            {successful && <Button variant="contained" color="success" onClick={resetForm}>Add another</Button>}
          </Stack>
        </Box>

        <Divider />

        <Box component="form" onSubmit={handleRegister} noValidate sx={{ p: { xs: 2, sm: 3 }, overflowX: 'hidden', width: '100%' }}>
          {(localMessage || message) && (
            <Box sx={{ mb: 2.5 }}>
              <Alert severity={successful ? "success" : "error"}>{localMessage || message}</Alert>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} sx={{ width: '100%' }}>
              <Grid container spacing={2}>

                {/* Required fields group (sequential) */}
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Username" variant="outlined" value={username} onChange={onChangeUsername} required />
                  {renderError('username')}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={onChangeEmail}
                    helperText="Login credentials & portal URL will be sent to this email"
                    required
                  />
                  {renderError('email')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Registration Number"
                    variant="outlined"
                    value={registrationNo}
                    onChange={onChangeRegistrationNo}
                    placeholder="e.g. 000101"
                    inputProps={{ inputMode: "numeric", maxLength: 6 }}
                    helperText="Manual entry: exactly 6 digits (must be unique)"
                    required
                  />
                  {renderError('registrationNo')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Membership ID"
                    variant="outlined"
                    value="Auto-generated (6 digits)"
                    InputProps={{ readOnly: true }}
                    helperText="Automatically generated incrementally on save (e.g. 000001)"
                    disabled
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Date of Birth" type="date" variant="outlined" value={date} onChange={onChangeDate} InputLabelProps={{ shrink: true }} inputProps={{ max: maxDob }} required />
                  {renderError('date')}
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="First Name" variant="outlined" value={firstName} onChange={onChangeFirstName} required />
                  {renderError('firstName')}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Middle Name" variant="outlined" value={middleName} onChange={onChangeMiddleName} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Last Name" variant="outlined" value={lastName} onChange={onChangeLastName} required />
                  {renderError('lastName')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Phone Number" variant="outlined" value={phoneNumber} onChange={onChangePhoneNumber} required />
                  {renderError('phoneNumber')}
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select value={sex} label="Gender" onChange={onChangeSex}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                    {renderError('sex')}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Marital Status</InputLabel>
                    <Select value={maritalStatus} label="Marital Status" onChange={onChangeMaritalStatus}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="Single">Single</MenuItem>
                      <MenuItem value="Married">Married</MenuItem>
                      <MenuItem value="Divorce">Divorce</MenuItem>
                      <MenuItem value="Widowed">Widowed</MenuItem>
                      <MenuItem value="Others">Others</MenuItem>
                    </Select>
                    {renderError('maritalStatus')}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Residence Address" variant="outlined" value={residenceAddress} onChange={onChangeResidenceAddress} required />
                  {renderError('residenceAddress')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Occupation" variant="outlined" value={applicantsPlaceOfWork} onChange={onChangeApplicantsPlaceOfWork} required />
                  {renderError('applicantsPlaceOfWork')}
                </Grid>

                {suggestedRefereeMatch && (!refereePhoneNumber || !refereeEmail) && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#F0FDF4",
                        border: "1px solid #86EFAC",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ backgroundColor: "#DCFCE7", p: 1, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <AutoAwesome sx={{ fontSize: 20, color: "#166534" }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534", fontSize: "0.85rem", lineHeight: 1.3 }}>
                            Suggested Match: {suggestedRefereeMatch.fullName} {suggestedRefereeMatch.regNo ? `(#${suggestedRefereeMatch.regNo})` : ""}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#15803D", display: "block" }}>
                            Phone: <strong>{suggestedRefereeMatch.raw?.phoneNumber || "N/A"}</strong> • Email: <strong>{suggestedRefereeMatch.raw?.email || "N/A"}</strong>
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleApplySuggestedReferee(suggestedRefereeMatch)}
                        sx={{
                          backgroundColor: "#166534",
                          color: "#ffffff",
                          textTransform: "none",
                          fontSize: "0.78rem",
                          fontWeight: 800,
                          py: 0.7,
                          px: 2,
                          borderRadius: 1.5,
                          boxShadow: "0 2px 6px rgba(22, 101, 52, 0.25)",
                          "&:hover": { backgroundColor: "#14532D" },
                        }}
                      >
                        Apply Auto-Fill
                      </Button>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={refereeOptions}
                    loading={loadingReferees}
                    value={nameOfReferee || ""}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "object" && newValue !== null) {
                        setNameOfReferee(newValue.fullName || newValue.label || "");
                        if (newValue.raw?.phoneNumber) setRefereePhoneNumber(newValue.raw.phoneNumber);
                        if (newValue.raw?.email) setRefereeEmail(newValue.raw.email);
                        if (newValue.raw?.residenceAddress || newValue.raw?.postalAddress) {
                          setRefereeAddress(newValue.raw.residenceAddress || newValue.raw.postalAddress || "");
                        }
                      } else if (typeof newValue === "string") {
                        setNameOfReferee(newValue);
                      } else if (!newValue) {
                        setNameOfReferee("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setNameOfReferee(newInputValue);
                      }
                    }}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option?.label || option?.fullName || "";
                    }}
                    isOptionEqualToValue={(option, val) => {
                      if (typeof val === "string") {
                        return option.fullName === val || option.label === val;
                      }
                      return option.id === val?.id;
                    }}
                    filterOptions={(options, state) => {
                      const query = (state.inputValue || "").toLowerCase().trim();
                      if (!query) return options;
                      return options.filter((opt) => {
                        const label = (opt.label || "").toLowerCase();
                        const reg = (opt.regNo || "").toLowerCase();
                        const full = (opt.fullName || "").toLowerCase();
                        return label.includes(query) || reg.includes(query) || full.includes(query);
                      });
                    }}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        key={option.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          px: 1.5,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                            {option.fullName}
                          </Typography>
                          {option.regNo ? (
                            <Typography variant="caption" sx={{ color: "#0D9488", fontWeight: 600 }}>
                              ID / Reg: {option.regNo}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                              Registered Member
                            </Typography>
                          )}
                        </Box>
                        {option.raw?.email && (
                          <Typography variant="caption" sx={{ color: "#64748B", ml: 1 }}>
                            {option.raw.email}
                          </Typography>
                        )}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Referee Name *"
                        variant="outlined"
                        required
                        placeholder="Type to search existing member..."
                        helperText="Select existing member to auto-fill phone, email & address"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingReferees ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {renderError('nameOfReferee')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select value={state} label="State" onChange={onChangeState}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {NIGERIAN_STATES.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                    {renderError('state')}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Home Town" variant="outlined" value={homeTown} onChange={onChangeHomeTown} />
                  {renderError('homeTown')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Postal Address" variant="outlined" value={postalAddress} onChange={onChangePostalAddress} />
                  {renderError('postalAddress')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Applicant Date" type="date" variant="outlined" value={applicantDate} onChange={onChangeApplicantDate} InputLabelProps={{ shrink: true }} />
                  {renderError('applicantDate')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Referee Email" type="email" variant="outlined" value={refereeEmail} onChange={onChangeRefereeEmail} />
                  {renderError('refereeEmail')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Referee Address" variant="outlined" value={refereeAddress} onChange={onChangeRefereeAddress} />
                  {renderError('refereeAddress')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Referee Phone" variant="outlined" value={refereePhoneNumber} onChange={onChangeRefereePhoneNumber} />
                  {renderError('refereePhoneNumber')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Referee Date" type="date" variant="outlined" value={refereeDate} onChange={onChangeRefereeDate} InputLabelProps={{ shrink: true }} />
                  {renderError('refereeDate')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Membership Status</InputLabel>
                    <Select value={approvedOrNotApproved || "Active"} label="Membership Status" onChange={onChangeApprovedOrNotApproved}>
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Non-Active">Non-Active</MenuItem>
                      <MenuItem value="Withdraw">Withdraw</MenuItem>
                      <MenuItem value="Expel">Expel</MenuItem>
                      <MenuItem value="Decease">Decease</MenuItem>
                    </Select>
                    {renderError('approvedOrNotApproved')}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Status Date" type="date" variant="outlined" value={statusDate} onChange={onChangeStatusDate} InputLabelProps={{ shrink: true }} />
                  {renderError('statusDate')}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordManuallyEdited(true);
                    }}
                    required
                    helperText="Auto-generated from username + last 3 digits of reg. no. You may change it."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                          <Tooltip title="Copy password">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(password)}
                                disabled={!password}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {renderError('password')}
                </Grid>

                {/* Mobile-only Credentials Dispatch & Password Card */}
                {generatedPassword && (
                  <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                            Auto-Generated Password:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0B3B60', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                            {generatedPassword}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy password">
                          <IconButton size="small" onClick={() => copyToClipboard(generatedPassword)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#F0FDF4', borderRadius: 1, border: '1px solid #BBF7D0' }}>
                        <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, display: 'block' }}>
                          ✉ Automated Dispatch:
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#15803D', display: 'block' }}>
                          Username, password & login link (https://mtcfs.netlify.app/login) will be automatically sent to <strong>{email || "member's email"}</strong>.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <LoadingButton
                    type="submit"
                    loading={loading}
                    variant="contained"
                    sx={{ bgcolor: 'primary.main', width: { xs: '100%', sm: 'auto' } }}
                  >
                    Register New Member
                  </LoadingButton>
                  <Button component={Link} to="/dashboard" variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    Cancel
                  </Button>
                </Grid>

                {(localMessage || message) && (
                  <Grid item xs={12}>
                    <Alert severity={successful ? "success" : "error"}>{localMessage || message}</Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' }, mt: { xs: 2, md: 0 } }}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <PersonAddIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Member Preview</Typography>
                      <Typography variant="body2" color="text.secondary">{firstName} {lastName}</Typography>
                      <Typography variant="body2" color="text.secondary">Username: {username || '—'}</Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Generated password</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Box sx={{ bgcolor: 'grey.100', p: 1.25, borderRadius: 1, flex: 1, wordBreak: 'break-all', minWidth: 0 }}>
                        <Typography variant="body2">{generatedPassword || 'Will be generated'}</Typography>
                      </Box>
                      <Tooltip title="Copy generated password">
                        <IconButton size="small" onClick={() => copyToClipboard(generatedPassword)} disabled={!generatedPassword}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Password = username + last 3 chars of registration number</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Quick details</Typography>
                    <Typography variant="body2" color="text.secondary">Phone: {phoneNumber || '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">State: {state || '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">Membership Status: {approvedOrNotApproved || 'Active'}</Typography>
                    <Typography variant="body2" color="text.secondary">Assigned To: Financial I (Automatic)</Typography>
                    <Box sx={{ mt: 1, p: 1, backgroundColor: "#F0FDF4", borderRadius: 1, border: "1px solid #BBF7D0" }}>
                      <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700, display: "block" }}>
                        Credentials Dispatch:
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#15803D", display: "block" }}>
                        Username, password & login URL (https://mtcfs.netlify.app/login) will be automatically emailed to <strong>{email || "member's email"}</strong>.
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default SignupForm;
